// src/pages/Platform/Tabs/Chatbot/ChatbotWidget/ChatInput.js

import React, { useState, useEffect } from 'react';
import Picker from 'emoji-picker-react';

// Minimal outline-style SVG icons
const EmojiIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <line x1="9" y1="9" x2="9.01" y2="9" />
    <line x1="15" y1="9" x2="15.01" y2="9" />
  </svg>
);

const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 2L11 13" />
    <path d="M22 2L15 22L11 13L2 9L22 2Z" />
  </svg>
);

const ChatInput = ({
  inputText,
  setInputText,
  onSendMessage,
  onInputChange,
  messagePrompt,
  isSending,
  inputRef,
  chatBackgroundColor,
  headerColor,
  activeRequiredForm,

  showPoweredBy = false,
  forceDisabled = false
}) => {
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);

  // Mobile keyboard handling - prevents content from being pushed up on iOS
  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        const keyboardHeight = window.innerHeight - window.visualViewport.height;
        document.documentElement.style.setProperty(
          '--keyboard-height',
          `${keyboardHeight}px`
        );
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      return () => window.visualViewport.removeEventListener('resize', handleResize);
    }
  }, []);

  const onEmojiClick = (emojiObject) => {
    setInputText((prevInput) => prevInput + emojiObject.emoji);
    setIsEmojiPickerOpen(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSendMessage(e);
  };

  // Don't render input if there's an active required form
  if (activeRequiredForm) {
    return null;
  }

  const isDisabled = isSending || inputText.trim() === '';

  // Calculate text color for powered-by based on chat background
  const getContrastColor = (hexColor) => {
    if (!hexColor) return '#666666';
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.55)';
  };

  const poweredByColor = getContrastColor(chatBackgroundColor);

  return (
    <div className="chatbot-footer-container">
      {isEmojiPickerOpen && (
        <div className="emoji-picker">
          <Picker onEmojiClick={onEmojiClick} />
        </div>
      )}

      <form className="chatbot-message-input" onSubmit={handleSubmit}>
        <input
          type="text"
          value={inputText}
          onChange={onInputChange}
          placeholder={messagePrompt || "Message..."}
          disabled={isSending || forceDisabled}
          ref={inputRef}
        />

        <div className="input-icons">
          <button
            type="button"
            className="icon-btn emoji-btn"
            onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
            aria-label="Insert Emoji"
          >
            <EmojiIcon />
          </button>

          <button
            type="submit"
            className="icon-btn send-btn"
            disabled={isDisabled}
            aria-label="Send Message"
          >
            <SendIcon />
          </button>
        </div>
      </form>

      {showPoweredBy && (
        <div className="powered-by-inline">
          <span style={{ color: poweredByColor }}>Powered by </span>
          <a
            href="https://www.smushlabs.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="powered-by-link"
          >
            <img
              src="https://smushlabs.ai/favicon.ico"
              alt="SmushLabs"
              width="12"
              height="12"
              style={{ verticalAlign: 'middle', opacity: 0.6 }}
            />
            <span>SmushLabs</span>
          </a>
        </div>
      )}
    </div>
  );
};

export default ChatInput; 